import { formatDateYMDToDMY, formatList, formatYesNo } from "utils";
import { toSentenceCase } from "utils/string";
import toCurrency from "utils/toCurrency";

import { currencyField, externalLinkField, readOnlyField } from "../fieldBuilders";

const formatCpv = (opportunity) => {
  if (!opportunity.cpvCode && !opportunity.cpvDescription) {
    return "-";
  }
  if (opportunity.cpvCode && opportunity.cpvDescription) {
    return `${opportunity.cpvCode} — ${opportunity.cpvDescription}`;
  }
  return opportunity.cpvCode || opportunity.cpvDescription;
};

const formatNoticeType = (opportunity) => {
  if (!opportunity.noticeType && !opportunity.noticeTypeDescription) {
    return "-";
  }
  if (opportunity.noticeType && opportunity.noticeTypeDescription) {
    return `${opportunity.noticeType}: ${opportunity.noticeTypeDescription}`;
  }
  return opportunity.noticeType || opportunity.noticeTypeDescription;
};

const getFindTenderRecordUrl = (opportunity) =>
  opportunity.ocid
    ? `https://www.find-tender.service.gov.uk/procurement/${opportunity.ocid}`
    : null;

const formatEstimatedValue = (opportunity) => {
  if (opportunity.estimatedValue === null || opportunity.estimatedValue === undefined) {
    return "-";
  }
  const formatted = toCurrency(opportunity.estimatedValue, {
    currency: opportunity.currency || "GBP",
  });
  return opportunity.currency ? `${formatted} ${opportunity.currency}` : formatted;
};

const buildFindATenderGroups = ({
  opportunity,
  formData,
  onChangeValue,
  focusField,
  defaultCurrencyCode,
}) => {
  const isEstimatedValueEditable = !opportunity.estimatedValueIsFromSource;

  const estimatedValueField = isEstimatedValueEditable
    ? currencyField("estimatedValue", "Amount", formData, onChangeValue, {
        currencyCode: defaultCurrencyCode,
        autoFocus: focusField === "estimatedValue",
      })
    : readOnlyField("estimatedValue", "Amount", formatEstimatedValue(opportunity));

  return [
    {
      title: "Fully Editable",
      fields: [
        ...(isEstimatedValueEditable ? [estimatedValueField] : []),
        externalLinkField(formData, onChangeValue),
      ],
    },
    {
      title: "Synced from Find a Tender",
      subtitle: "Read only - data extracted from Find a Tender",
      fields: [
        ...(isEstimatedValueEditable ? [] : [estimatedValueField]),
        readOnlyField("buyerName", "Contracting Authority", opportunity.buyerName),
        readOnlyField("buyerType", "Buyer Type", opportunity.buyerType),
        readOnlyField("id", "Notice ID", opportunity.id),
        readOnlyField("ocid", "OCID", opportunity.ocid),
        readOnlyField("cpvCode", "CPV Code", formatCpv(opportunity)),
        readOnlyField(
          "additionalCpvCodes",
          "Additional CPVs",
          formatList(opportunity.additionalCpvCodes)
        ),
        readOnlyField("procurementStage", "Procurement Stage", opportunity.procurementStage),
        readOnlyField(
          "frameworkAgreement",
          "Framework Agreement",
          formatYesNo(opportunity.frameworkAgreement)
        ),
        readOnlyField("numberOfLots", "Number of Lots", opportunity.numberOfLots),
        readOnlyField("suitability", "Suitability", formatList(opportunity.suitability, ", ")),
        readOnlyField("noticeType", "Notice Type", formatNoticeType(opportunity)),
        readOnlyField("procedureType", "Procedure Type", opportunity.procedureType),
        readOnlyField(
          "publishedDate",
          "Publication Date",
          formatDateYMDToDMY(opportunity.publishedDate)
        ),
        readOnlyField(
          "submissionDeadline",
          "Closing Date",
          formatDateYMDToDMY(opportunity.submissionDeadline)
        ),
        readOnlyField(
          "enquiryDeadline",
          "Enquiry Deadline",
          formatDateYMDToDMY(opportunity.enquiryDeadline)
        ),
        readOnlyField(
          "deliveryRegion",
          "Delivery Region",
          formatList(opportunity.deliveryRegion)
        ),
        readOnlyField("status", "Status", toSentenceCase(opportunity.status)),
        readOnlyField("link", "Find A Tender Record", getFindTenderRecordUrl(opportunity)),
      ],
    },
  ];
};

export default buildFindATenderGroups;
